<template>
  <div>
    <app-alert
      v-if="serverError"
      variant="danger"
      icon="error"
      dismissible
      @dismissed="serverError = ''"
    >{{ serverError }}</app-alert>

    <form @submit.stop.prevent="onSubmit" class="de-form">
      <app-alert
        v-if="delivery.partnerDeliverySyncError"
        variant="danger"
        icon="error"
        dismissible
        class="de-form__alert"
      >
        <strong>Error de Sincronización:</strong>
        {{ delivery.partnerDeliverySyncError }}
      </app-alert>

      <div class="de-form__grid">
        <app-input
          v-model="form.customerName"
          label="Nombre del Cliente"
          placeholder="Nombre completo"
          prepend-icon="person"
          :error-message="$v.form.customerName.$dirty && $v.form.customerName.$invalid ? 'Este campo es requerido' : ''"
          maxlength="150"
        />
        <app-input
          v-model="form.customerPhone"
          label="Teléfono Cliente"
          placeholder="912345678"
          prepend-icon="phone"
          type="tel"
        />
        <app-input
          v-model="form.customerEmail"
          label="Email Cliente"
          placeholder="cliente@email.com"
          prepend-icon="email"
          type="email"
        />
        <app-input
          v-model="form.address.street"
          label="Calle / Dirección"
          placeholder="Av. Providencia 1234"
          prepend-icon="place"
          maxlength="256"
        />
        <app-input
          v-model="form.address.commune"
          label="Comuna"
          placeholder="Providencia"
          prepend-icon="location_city"
          maxlength="256"
        />
        <app-select
          v-model="form.driverId"
          label="Conductor"
          :options="driverOptions"
          placeholder="Seleccionar Conductor"
        />
        <app-select
          v-model="form.storeId"
          label="Tienda"
          :options="storeOptions"
          placeholder="Seleccionar Tienda"
        />
        <app-input
          v-model="form.date"
          label="Fecha de Envío"
          type="date"
          prepend-icon="calendar_today"
          :error-message="$v.form.date.$dirty && $v.form.date.$invalid ? 'Este campo es requerido' : ''"
        />
        <app-select
          v-model="form.partnerCode"
          label="Partner"
          :options="partnerOptions"
          placeholder="Sin Partner"
        />
        <app-input
          v-model="form.partnerDeliveryId"
          label="Código Seguimiento Partner"
          placeholder="ID de seguimiento"
          prepend-icon="local_shipping"
          maxlength="256"
        />
        <app-select
          v-model="form.partnerDeliveryStatus"
          label="Estado Partner"
          :options="partnerDeliveryStatusOptions"
          placeholder="Sin estado"
        />
        <app-select
          v-model="form.state"
          label="Estado del Envío"
          :options="stateOptions"
        />
        <app-input
          v-model="form.receiverName"
          label="Recibido por"
          placeholder="Nombre de quien recibe"
          prepend-icon="person"
          maxlength="256"
        />
        <app-input
          v-model="form.receiverRut"
          label="RUT"
          placeholder="12.345.678-9"
          prepend-icon="badge"
          maxlength="256"
        />
        <app-input
          v-model="form.externalId"
          label="ID Externo"
          placeholder="ID único de sistema externo"
          prepend-icon="tag"
          maxlength="256"
        />
      </div>

      <app-textarea
        v-model="form.partnerDeliverySyncError"
        label="Error de Sincronización Partner"
        placeholder="Mensaje de error de sincronización..."
        :rows="2"
        maxlength="512"
        class="de-form__textarea"
      />

      <div class="de-form__switch-row">
        <app-switch v-model="form.hide" label="Ocultar este envío" variant="secondary" />
        <delivery-state-badge :state="form.state" />
      </div>

      <div v-if="form.partnerCode || delivery.lastPartnerSyncAt || delivery.partnerDeliveryId" class="de-form__partner-info">
        <div class="de-form__partner-header">
          <span class="material-icons de-form__partner-icon">sync</span>
          <strong>Información de Sincronización</strong>
        </div>
        <div v-if="delivery.lastPartnerSyncAt" class="de-form__partner-detail">
          Última Sincronización: {{ $moment(delivery.lastPartnerSyncAt).format('DD-MM-YYYY HH:mm') }}
        </div>
        <div v-if="delivery.partnerDeliveryId" class="de-form__partner-detail">
          Código Seguimiento Registrado: {{ delivery.partnerDeliveryId }}
        </div>
        <app-button
          v-if="delivery.partnerDeliveryId && delivery.partnerCode"
          variant="light"
          icon="open_in_new"
          size="sm"
          @click="goToPartnerDeliveryPage(delivery.partnerDeliveryId, delivery.partnerCode)"
        >
          Ver en Partner
        </app-button>
      </div>

      <app-textarea
        v-model="form.commentary"
        label="Comentario"
        placeholder="Notas adicionales del envío..."
        :rows="3"
        maxlength="256"
        class="de-form__textarea"
      />

      <div class="de-form__actions">
        <app-button
          variant="primary"
          outline
          label="Cancelar"
          @click="$emit('cancel')"
        />
        <app-button
          variant="secondary"
          type="submit"
          label="Guardar Cambios"
          icon="save"
          :loading="loading"
        />
      </div>
    </form>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { required } from 'vuelidate/lib/validators'
import MakeToastMixin from '../mixins/MakeToastMixin'
import phoneNumberFormatter from '../../shared/phoneNumberFormatter'
import partnerUtils from '../../utils/partnerUtils'
import AppInput from '@/components/UI/AppInput'
import AppSelect from '@/components/UI/AppSelect'
import AppTextarea from '@/components/UI/AppTextarea'
import AppButton from '@/components/UI/AppButton'
import AppSwitch from '@/components/UI/AppSwitch'
import AppAlert from '@/components/UI/AppAlert'
import DeliveryStateBadge from '@/components/UI/DeliveryStateBadge'

export default {
  name: 'DeliveryAdvancedEditForm',
  mixins: [MakeToastMixin],
  components: {
    AppInput,
    AppSelect,
    AppTextarea,
    AppButton,
    AppSwitch,
    AppAlert,
    DeliveryStateBadge
  },
  props: {
    delivery: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      partnerUtils,
      loading: false,
      serverError: '',
      form: {
        customerName: this.delivery.customerName || '',
        customerPhone: this.delivery.customerPhone || '',
        customerEmail: this.delivery.customerEmail || '',
        driverId: this.delivery.driverId || null,
        storeId: this.delivery.storeId || null,
        date: this.delivery.date ? this.$moment(this.delivery.date.substring(0, 10)).format('YYYY-MM-DD') : '',
        state: this.delivery.state || null,
        commentary: this.delivery.commentary || '',
        externalId: this.delivery.externalId || '',
        hide: !!this.delivery.hide,
        partnerCode: this.delivery.partnerCode || null,
        partnerDeliveryId: this.delivery.partnerDeliveryId || null,
        partnerDeliveryStatus: this.delivery.partnerDeliveryStatus || null,
        partnerDeliverySyncError: this.delivery.partnerDeliverySyncError || null,
        receiverName: this.delivery.receiverName || '',
        receiverRut: this.delivery.receiverRut || '',
        address: {
          commune: this.delivery.address ? this.delivery.address.commune || '' : '',
          street: this.delivery.address ? this.delivery.address.street || '' : ''
        }
      }
    }
  },
  validations: {
    form: {
      customerName: { required },
      date: { required }
    }
  },
  computed: {
    ...mapGetters(['stores', 'users', 'user']),
    partnerOptions() {
      return [
        { value: null, text: 'Sin Partner' },
        ...partnerUtils.partnerSelectOptions
      ]
    },
    partnerDeliveryStatusOptions() {
      const statusLabels = partnerUtils.partnerDeliveryStatusLabels[this.form.partnerCode]
      if (!statusLabels) return [{ value: null, text: 'Sin estado' }]
      const options = [{ value: null, text: 'Sin estado' }]
      Object.entries(statusLabels).forEach(([value, label]) => {
        options.push({ value, text: label })
      })
      return options
    },
    storeOptions() {
      const stores = [{ value: null, text: 'Seleccionar Tienda' }]
      this.stores.forEach(store => {
        stores.push({ value: store.id, text: store.name })
      })
      return stores
    },
    driverOptions() {
      const drivers = [{ value: null, text: 'Seleccionar Conductor' }]
      const deliveryUsers = this.users.filter(u => u.role === 'delivery')
      deliveryUsers.forEach(user => {
        drivers.push({ value: user.id, text: `${user.name} ${user.lastName}` })
      })
      return drivers
    },
    stateOptions() {
      return [
        { value: null, text: 'Por entregar' },
        { value: 'entregado', text: 'Entregado' },
        { value: 'pendiente', text: 'Pendiente' },
        { value: 'rechazado', text: 'Rechazado' },
        { value: 'cancelado', text: 'Cancelado' }
      ]
    }
  },
  methods: {
    ...mapActions(['updateDelivery', 'listUser', 'listStore']),
    phoneNumberFormatter,
    goToPartnerDeliveryPage(deliveryPartnerId, partnerCode) {
      const partnerDeliveryLink = {
        [partnerUtils.partners.FAZT]: `https://staging-panel.fazt.cl/shipments/${deliveryPartnerId}`,
        [partnerUtils.partners.DOCE_HORAS_ENVIOS]: `https://socios.12horasenvios.cl/envios-tienda?search=${deliveryPartnerId}`
      }
      window.open(partnerDeliveryLink[partnerCode], '_blank')
    },
    async onSubmit() {
      this.serverError = ''
      this.$v.$touch()
      if (this.$v.$invalid) return

      this.loading = true
      try {
        const data = { ...this.form }
        const response = await this.updateDelivery({ id: this.delivery.id, data })
        if (response.status === 200) {
          this.makeSuccessToast('El envío se editó correctamente')
          this.$emit('done')
        } else if (response.status >= 400) {
          this.serverError = response.data.message || 'Error al editar el envío'
        }
      } catch (err) {
        this.serverError = 'Error de conexión al editar el envío'
      } finally {
        this.loading = false
      }
    }
  },
  async created() {
    if (this.user.role === 'admin' || this.user.role === 'delivery') {
      this.listUser()
      this.listStore()
    }
  }
}
</script>

<style scoped>
.de-form {
}

.de-form__alert {
  margin-bottom: 16px;
}

.de-form__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

@media (max-width: 768px) {
  .de-form__grid {
    grid-template-columns: 1fr;
  }
}

.de-form__textarea {
  margin-bottom: 16px;
}

.de-form__switch-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
}

.de-form__partner-info {
  background-color: #f8f9fa;
  border-radius: 0.375rem;
  padding: 14px 16px;
  margin-bottom: 16px;
  font-size: 14px;
}

.de-form__partner-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.de-form__partner-icon {
  color: #4AAE9B;
  font-size: 20px;
}

.de-form__partner-detail {
  margin-bottom: 4px;
}

.de-form__partner-detail:last-of-type {
  margin-bottom: 8px;
}

.de-form__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
}
</style>
